import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import * as d3 from 'd3';
import './UkMapLeaflet.css';
import { useNavigate } from 'react-router-dom';
import { categoryConfigs, applicationProgressConfig } from './UkMapSettings';


function UkMapLeaflet({mapHeight = "600px", data}) {
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const navigate = useNavigate();
  const [geoData, setGeoData] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedDecision, setSelectedDecision] = useState(null);
  const zOrder = {          
    Approved: 20000,         
    'In Progress': 10000,   
    Rejected: 0 
  };

  const resetFilters = () => {
    if (!mapRef.current) return;
    setSelectedCategory(null);
    setSelectedDecision(null);
    mapRef.current.setView([53, 1], 7); 
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const ukJson = await d3.json('/uk.geojson');
        setGeoData(ukJson);
      } catch (err) {
        console.error('Error loading data:', err);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!geoData || mapRef.current) return;

    const map = L.map('uk-map-leaflet', {
      center: [53, 1],
      zoom: 7,
      scrollWheelZoom: false,   // desktop wheel
      dragging: false,          // pan
      touchZoom: false,         // pinch
      doubleClickZoom: false,
      tap: false                // legacy iOS helper
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 14,
      minZoom: 6,
      attribution: '&copy; OpenStreetMap'
    }).addTo(map);

    mapRef.current = map;

    function enableInteractive() {
      map.scrollWheelZoom.enable();
      map.dragging.enable();
      map.touchZoom.enable();
      map.doubleClickZoom.enable();
      map.tap && map.tap.enable();

      document
        .getElementById('uk-map-leaflet')
        .classList.remove('locked');

      window.addEventListener('scroll', disableInteractive, { once: true });
    }

    function disableInteractive() {
      map.scrollWheelZoom.disable();
      map.dragging.disable();
      map.touchZoom.disable();
      map.doubleClickZoom.disable();
      map.tap && map.tap.disable();

      document
        .getElementById('uk-map-leaflet')
        .classList.add('locked');

      map.once('click', enableInteractive);     // wait for next tap
    }

    map.once('click', enableInteractive);

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [geoData]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !data || data.length === 0) return;

    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    const locations = data
      .filter(r => r.ProjectReference?.trim())          
      .sort((a, b) => (a.Decision ?? "").localeCompare(
                        b.Decision ?? "",
                        undefined,
                        { sensitivity: "base" }         
                      ));

    locations.forEach((location) => {
      if (
        (selectedCategory && selectedCategory !== location.ApplicationCategory) ||
        (selectedDecision && selectedDecision !== location.Decision)
      ) return;

      const marker = createMarker(location, navigate, zOrder[location.Decision] || 0);
      if (marker) {
        marker.addTo(map);
        markersRef.current.push(marker);
      }
    });

    const counts = locations.reduce((acc, loc) => {
      const category = loc.ApplicationCategory || 'Unknown';
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    }, {});    

    addLegend(map, selectedCategory, setSelectedCategory, counts);
    addDecisionLegend(map, selectedDecision, setSelectedDecision);
    addResetControl(map, resetFilters);

  }, [data, selectedCategory, selectedDecision, navigate, zOrder]);

  return (
    <div
      id="uk-map-leaflet"
      className="locked"    
      style={{ width: '100%', height: mapHeight, position: 'relative' }}
    />
  );
}

function createMarker(data, navigate, zIndexOffset = 0) {
  const lat = parseFloat(data.latitude);
  const lng = parseFloat(data.longitude);
  if (isNaN(lat) || isNaN(lng)) return null;

  const config = categoryConfigs[data.ApplicationCategory] || categoryConfigs.default;
  const markerConfig = applicationProgressConfig[data.Decision] || applicationProgressConfig.default

  const svgIconHTML = `
    <div class="custom-marker" style="color: ${markerConfig.color}">
      <svg width="24" height="36" viewBox="0 0 24 36" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 0C5.4 0 0 5.4 0 12c0 7.2 6 15.6 12 24 6-8.4 12-16.8 12-24C24 5.4 18.6 0 12 0z" fill="currentColor"/>
      </svg>
      <i class="fa fa-${config.icon} marker-icon"></i>
    </div>
  `;

  const icon = L.divIcon({
    className: '',
    html: svgIconHTML,
    iconSize: [24, 36],
    iconAnchor: [12, 36],
    popupAnchor: [0, -36],
  });

  const marker = L.marker([lat, lng], { icon, zIndexOffset });
  const imageURL = data.ImageURL.replace(/\s+/g, '').replace('/images/','/thumbs/');
  marker.bindTooltip(
    `
      <div class="tooltip-content">
        <img src="${imageURL}" alt="Project Image" class="tooltip-image" />
        <strong>Project:</strong> ${data.ProjectName}<br/><br/>
        <strong>Project type:</strong> ${data.ApplicationType}<br/><br/>
        <strong>Description:</strong> ${clipText(data.Description, 300)}
      </div>
    `,
    { className: 'marker-tooltip' }
  );

  marker.on('click', (e) => {
    const mapElement = document.getElementById('uk-map-leaflet');
    if (mapElement.classList.contains('locked')) {
      // If map is locked, stop propagation and let the map's click handler handle it
      e.originalEvent.stopPropagation();
      return;
    }
    // Only navigate if map is unlocked
    navigate(`/project/${data.ProjectReference}`, { state: data });
  });

  return marker;
}

function addResetControl(map, resetFilters) {
  if (map.resetControl) return;

  const resetControl = L.control({ position: 'topleft' });

  resetControl.onAdd = function () {
    const div = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-custom');
    div.innerHTML = '🔄 Reset view';
    div.title = 'Reset view';
    div.style.backgroundColor = 'white';
    div.style.width = '110px';
    div.style.height = '34px';
    div.style.display = 'flex';
    div.style.alignItems = 'center';
    div.style.justifyContent = 'center';
    div.style.fontSize = '14px';
    div.style.cursor = 'pointer';

    div.onclick = () => {
      resetFilters();
    };

    return div;
  };

  resetControl.addTo(map);
  map.resetControl = resetControl;
}

function addDecisionLegend(map, selectedDecision, setSelectedDecision) {
  if (map.decisionLegendControl) {
    map.removeControl(map.decisionLegendControl);
  }

  const legendControl = L.control({ position: 'bottomright' });

  legendControl.onAdd = function () {
    const div = L.DomUtil.create('div', 'leaflet-legend');
    div.style.padding = '6px 8px';
    div.style.background = 'white';
    div.innerHTML = `<strong>Status</strong><br/>`;

    Object.entries(applicationProgressConfig)
        .filter(([status]) => status !== 'default')
        .forEach(([status, config]) => {
      const item = document.createElement('div');
      item.className = 'legend-item';
      if (selectedDecision === status) item.classList.add('active');
      item.style.display = 'flex';
      item.style.alignItems = 'center';
      item.style.marginTop = '6px';
      item.style.cursor = 'pointer';
      var label = status;
      if (status === "In Progress") label = "In Progress";

      item.innerHTML = `
        <div style="
          width: 14px;
          height: 14px;
          background: ${config.color};
          margin-right: 6px;
        "></div>
        <span>${label}</span>
      `;

      item.addEventListener('click', () => {
        setSelectedDecision(prev => (prev === status ? null : status));
      });

      div.appendChild(item);
    });

    return div;
  };

  legendControl.addTo(map);
  map.decisionLegendControl = legendControl;
}

function addLegend(map, selectedCategory, setSelectedCategory, counts = {}) {
  if (map.legendControl) {
    map.removeControl(map.legendControl);
  }

  const legendControl = L.control({ position: 'topright' });
  legendControl.onAdd = function () {
    const div = L.DomUtil.create('div', 'leaflet-legend');
    div.style.padding = '6px 8px';
    div.style.background = 'white';
    div.innerHTML = `<strong>Legend</strong><br/>`;
    const types = Object.keys(categoryConfigs).filter(type => counts[type]);
    types.forEach((type) => {
      const item = getLegendItem(type, selectedCategory, counts);

      item.addEventListener('click', () => {
        setSelectedCategory((prev) => (prev === type ? null : type));
      });

      div.appendChild(item);
    });

    return div;
  };

  legendControl.addTo(map);
  map.legendControl = legendControl;
}

function getLegendItem(type, selectedCategory, counts) {
  const cfg = categoryConfigs[type];
  const item = document.createElement('div');
  item.className = 'legend-item';
  if (selectedCategory === type) item.classList.add('active');
  item.style.display = 'flex';
  item.style.alignItems = 'center';
  item.style.marginTop = '6px';
  item.style.cursor = 'pointer';

  item.innerHTML = `
        <div style="
          width: 18px;
          height: 18px;
          background: ${cfg.color};
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 6px;
        ">
          <i class="fa fa-${cfg.icon}" style="color: white; font-size: 12px;"></i>
        </div>
        <span>${type} (${counts[type] || 0})</span>
      `;
  return item;
}

function clipText(text, maxLength) {
  if (!text) return '';
  if (text.length > maxLength) {
    let clipped = text.substring(0, maxLength);
    let lastSpace = clipped.lastIndexOf(' ');
    if (lastSpace > 0) {
      clipped = clipped.substring(0, lastSpace);
    }
    return clipped + '...';
  }
  return text;
}


export default UkMapLeaflet;
